// Pedidos Module - Vuex Store
// Manages state persistence for pedidos list view

const STORAGE_KEY = 'pedidos_state'

/**
 * Load state from sessionStorage
 */
function loadStateFromStorage() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error('Error loading pedidos state from sessionStorage:', error)
  }
  return null
}

/**
 * Save state to sessionStorage
 */
function saveStateToStorage(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error('Error saving pedidos state to sessionStorage:', error)
  }
}

const state = {
  filters: {
    startDate: null,
    endDate: null,
    store: '',
    status: '',
    search: '',
    paymentType: '',
    deliveryType: '',
    origin: '',
  },
  pagination: {
    page: 1,
    pageSize: 10,
  },
  scrollY: 0,
  hasActiveState: false,
  isRestored: false,
}

// Load initial state from sessionStorage if available
const storedState = loadStateFromStorage()
if (storedState) {
  Object.assign(state, storedState)
}

const getters = {
  filters: state => state.filters,
  pagination: state => state.pagination,
  scrollY: state => state.scrollY,
  hasActiveState: state => state.hasActiveState,
  isRestored: state => state.isRestored,
}

const mutations = {
  /**
   * Set filters
   */
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
    state.hasActiveState = true
    saveStateToStorage({
      filters: state.filters,
      pagination: state.pagination,
      scrollY: state.scrollY,
      hasActiveState: state.hasActiveState,
    })
  },

  /**
   * Set pagination
   */
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination }
    state.hasActiveState = true
    saveStateToStorage({
      filters: state.filters,
      pagination: state.pagination,
      scrollY: state.scrollY,
      hasActiveState: state.hasActiveState,
    })
  },

  /**
   * Set scroll position
   */
  SET_SCROLL(state, scrollY) {
    state.scrollY = scrollY
    if (state.hasActiveState) {
      saveStateToStorage({
        filters: state.filters,
        pagination: state.pagination,
        scrollY: state.scrollY,
        hasActiveState: state.hasActiveState,
      })
    }
  },

  /**
   * Set hasActiveState flag
   */
  SET_HAS_ACTIVE_STATE(state, value) {
    state.hasActiveState = value
    if (!value) {
      // Clear sessionStorage when resetting
      try {
        sessionStorage.removeItem(STORAGE_KEY)
      } catch (error) {
        console.error('Error clearing pedidos state from sessionStorage:', error)
      }
    } else {
      saveStateToStorage({
        filters: state.filters,
        pagination: state.pagination,
        scrollY: state.scrollY,
        hasActiveState: state.hasActiveState,
      })
    }
  },

  /**
   * Set isRestored flag
   */
  SET_IS_RESTORED(state, value) {
    state.isRestored = value
  },

  /**
   * Reset pedidos state to default values
   */
  RESET_PEDIDOS_STATE(state) {
    // Default: últimos 7 días
    const endDate = new Date()
    endDate.setHours(23, 59, 59, 999)
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 7)
    startDate.setHours(0, 0, 0, 0)

    state.filters = {
      startDate: startDate.getTime(),
      endDate: endDate.getTime(),
      store: '',
      status: '',
      search: '',
      paymentType: '',
      deliveryType: '',
      origin: '',
    }
    state.pagination = {
      page: 1,
      pageSize: 10,
    }
    state.scrollY = 0
    state.hasActiveState = false
    state.isRestored = false

    // Clear sessionStorage
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.error('Error clearing pedidos state from sessionStorage:', error)
    }
  },

  /**
   * Restore state from storage
   */
  RESTORE_STATE(state, restoredState) {
    if (restoredState) {
      state.filters = restoredState.filters || state.filters
      state.pagination = restoredState.pagination || state.pagination
      state.scrollY = restoredState.scrollY || 0
      state.hasActiveState = restoredState.hasActiveState || false
    }
    state.isRestored = true
  },
}

const actions = {
  /**
   * Save current scroll position
   */
  saveScroll({ commit }) {
    commit('SET_SCROLL', window.scrollY || 0)
  },

  /**
   * Reset state to default
   */
  resetState({ commit }) {
    commit('RESET_PEDIDOS_STATE')
  },

  /**
   * Restore state from storage
   */
  restoreState({ commit, state: currentState }) {
    const stored = loadStateFromStorage()
    if (stored) {
      commit('RESTORE_STATE', stored)
    } else {
      commit('SET_IS_RESTORED', true)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
